import React, { useState } from "react";
import {
  Avatar,
  Card,
  CardContent,
  Grid,
  Typography,
  CircularProgress,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link as MuiLink,
  Snackbar,
  Alert,
  Box,
  TextField,
  Divider,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  LinearProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { Link } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import DescriptionIcon from "@mui/icons-material/Description";
import FolderIcon from "@mui/icons-material/Folder";
import DownloadIcon from "@mui/icons-material/Download";
import SignatureIcon from "@mui/icons-material/Draw";
import VisibilityIcon from "@mui/icons-material/Visibility";
import BusinessIcon from "@mui/icons-material/Business";
import GroupIcon from "@mui/icons-material/Group";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import CreateIcon from "@mui/icons-material/Create";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import BalanceIcon from "@mui/icons-material/Balance";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import WarningIcon from "@mui/icons-material/Warning";
import client from "../../../API";

const MyTeam = () => {
  const queryClient = useQueryClient();
  const [selectedUser, setSelectedUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const [tabValue, setTabValue] = useState(0);
  
  // Team Management State
  const [teamDialogOpen, setTeamDialogOpen] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [teamFormData, setTeamFormData] = useState({
    name: "",
    description: "",
    team_leader_id: "",
    can_manage_members: false
  });
  
  // User Assignment State
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedTeamForAssignment, setSelectedTeamForAssignment] = useState(null);
  const [selectedUserForAssignment, setSelectedUserForAssignment] = useState("");
  
  // Credit Management State
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [creditRequestData, setCreditRequestData] = useState({
    requested_credit_line: "",
    requested_interest_rate: "",
    reason: ""
  });
  
  // Team Credit Management State
  const [teamCreditDialogOpen, setTeamCreditDialogOpen] = useState(false);
  const [selectedTeamForCredit, setSelectedTeamForCredit] = useState(null);
  const [teamCreditData, setTeamCreditData] = useState({
    allocated_credit_line: "",
    reason: ""
  });
  
  // Credit Rebalancing State
  const [rebalanceDialogOpen, setRebalanceDialogOpen] = useState(false);
  const [rebalanceData, setRebalanceData] = useState([]);
  
  // Team Credit Usage State
  const [creditUsageDialogOpen, setCreditUsageDialogOpen] = useState(false);
  const [creditUsageData, setCreditUsageData] = useState({
    amount: "",
    reason: "",
    operation: "use" // "use" or "release"
  });
  
  // Legal Entity Management State
  const [legalEntityDialogOpen, setLegalEntityDialogOpen] = useState(false);
  const [legalEntityName, setLegalEntityName] = useState("");
  
  // Contract Management State
  const [contractDialogOpen, setContractDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [contractSignatureDialogOpen, setContractSignatureDialogOpen] = useState(false);

  /* ─────────────────────────────── FETCH DATA ─────────────────────────────── */
  const {
    data: teamStructure,
    isLoading: teamLoading,
    error: teamError,
  } = useQuery("teamStructure", client.users.fetchMyTeam);

  const {
    data: companyData,
    isLoading: companyLoading,
  } = useQuery(
    ["company", teamStructure?.company_id],
    () => client.companies.listFetch({ id: teamStructure.company_id }),
    { enabled: !!teamStructure?.company_id }
  );

  const {
    data: creditSummary,
    isLoading: creditSummaryLoading,
  } = useQuery(
    ["creditSummary", companyData?.id],
    () => client.teams.getCreditSummary(),
    { enabled: !!companyData?.id }
  );

  const {
    data: creditLedger,
    isLoading: ledgerLoading,
  } = useQuery(
    ["creditLedger", companyData?.id],
    () => client.creditLedger.fetch(companyData.id),
    { enabled: !!companyData?.id }
  );

  const {
    data: contracts,
    isLoading: contractsLoading,
  } = useQuery(
    ["contracts", companyData?.id],
    () => client.contracts.fetchByCompany(companyData.id),
    { enabled: !!companyData?.id }
  );

  const {
    data: teamFiles,
    isLoading: filesLoading,
  } = useQuery(
    ["teamFiles", companyData?.id],
    () => client.files.fetchByCompany(companyData.id),
    { enabled: !!companyData?.id }
  );

  /* ─────────────────────────── MUTATIONS ─────────────────────────────── */
  const activateMutation = useMutation(
    (userId) => client.users.confirm(userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("teamStructure");
        setSnack({ open: true, msg: "User activated!", sev: "success" });
      },
      onError: () => {
        setSnack({ open: true, msg: "Activation failed.", sev: "error" });
      },
      onSettled: () => setDialogOpen(false),
    }
  );

  const createTeamMutation = useMutation(
    (data) => client.teams.create(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("teamStructure");
        queryClient.invalidateQueries("creditSummary");
        setTeamDialogOpen(false);
        resetTeamForm();
        setSnack({ open: true, msg: "Team created successfully!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Failed to create team", sev: "error" });
      },
    }
  );

  const updateTeamMutation = useMutation(
    ({ id, data }) => client.teams.update(id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("teamStructure");
        queryClient.invalidateQueries("creditSummary");
        setTeamDialogOpen(false);
        resetTeamForm();
        setSnack({ open: true, msg: "Team updated successfully!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Failed to update team", sev: "error" });
      },
    }
  );

  const assignUserMutation = useMutation(
    ({ teamId, userId }) => client.teams.assignUser(teamId, userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("teamStructure");
        setAssignDialogOpen(false);
        setSelectedUserForAssignment("");
        setSnack({ open: true, msg: "User assigned to team!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Failed to assign user", sev: "error" });
      },
    }
  );

  const removeUserMutation = useMutation(
    ({ teamId, userId }) => client.teams.removeUser(teamId, userId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("teamStructure");
        setSnack({ open: true, msg: "User removed from team!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Failed to remove user", sev: "error" });
      },
    }
  );

  const allocateTeamCreditMutation = useMutation(
    ({ teamId, data }) => client.teams.allocateCredit(teamId, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("teamStructure");
        queryClient.invalidateQueries("creditSummary");
        setTeamCreditDialogOpen(false);
        setTeamCreditData({ allocated_credit_line: "", reason: "" });
        setSnack({ open: true, msg: "Team credit allocated successfully!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Failed to allocate credit", sev: "error" });
      },
    }
  );

  const rebalanceCreditMutation = useMutation(
    (data) => client.teams.rebalanceCredit(data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("teamStructure");
        queryClient.invalidateQueries("creditSummary");
        setRebalanceDialogOpen(false);
        setSnack({ open: true, msg: "Credit rebalanced successfully!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Failed to rebalance credit", sev: "error" });
      },
    }
  );

  const manageCreditUsageMutation = useMutation(
    ({ teamId, operation, data }) => {
      if (operation === "use") {
        return client.teams.useCredit(teamId, data);
      } else {
        return client.teams.releaseCredit(teamId, data);
      }
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("teamStructure");
        queryClient.invalidateQueries("creditSummary");
        setCreditUsageDialogOpen(false);
        setCreditUsageData({ amount: "", reason: "", operation: "use" });
        setSnack({ open: true, msg: "Credit usage updated successfully!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Failed to update credit usage", sev: "error" });
      },
    }
  );

  const creditRequestMutation = useMutation(
    (data) => client.creditLedger.postRequest({
      company_id: companyData.id,
      ...data
    }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["creditLedger", companyData.id]);
        setCreditDialogOpen(false);
        setCreditRequestData({ requested_credit_line: "", requested_interest_rate: "", reason: "" });
        setSnack({ open: true, msg: "Credit request submitted!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Request failed", sev: "error" });
      },
    }
  );

  const updateLegalEntityMutation = useMutation(
    (data) => client.companies.updateLegalEntity(companyData.id, data),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["company", companyData.id]);
        setLegalEntityDialogOpen(false);
        setSnack({ open: true, msg: "Legal entity name updated!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Update failed", sev: "error" });
      },
    }
  );

  const signContractMutation = useMutation(
    (data) => client.contracts.sign(data.contractId, data.signature),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["contracts", companyData.id]);
        setContractSignatureDialogOpen(false);
        setSnack({ open: true, msg: "Contract signed successfully!", sev: "success" });
      },
      onError: (error) => {
        setSnack({ open: true, msg: error?.response?.data?.error || "Signing failed", sev: "error" });
      },
    }
  );

  /* ───────────────────────────────── HANDLERS ───────────────────────────────── */
  const openDialog = (user) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };

  const resetTeamForm = () => {
    setTeamFormData({
      name: "",
      description: "",
      team_leader_id: "",
      can_manage_members: false
    });
    setEditingTeam(null);
  };

  const handleCreateTeam = () => {
    setEditingTeam(null);
    resetTeamForm();
    setTeamDialogOpen(true);
  };

  const handleEditTeam = (team) => {
    setEditingTeam(team);
    setTeamFormData({
      name: team.name,
      description: team.description || "",
      team_leader_id: team.team_leader_id || "",
      can_manage_members: team.can_manage_members
    });
    setTeamDialogOpen(true);
  };

  const handleTeamSubmit = () => {
    if (editingTeam) {
      updateTeamMutation.mutate({ id: editingTeam.id, data: teamFormData });
    } else {
      createTeamMutation.mutate(teamFormData);
    }
  };

  const handleAssignUser = (team) => {
    setSelectedTeamForAssignment(team);
    setAssignDialogOpen(true);
  };

  const handleAssignSubmit = () => {
    assignUserMutation.mutate({
      teamId: selectedTeamForAssignment.id,
      userId: parseInt(selectedUserForAssignment)
    });
  };

  const handleRemoveUser = (teamId, userId) => {
    removeUserMutation.mutate({ teamId, userId });
  };

  const handleAllocateTeamCredit = (team) => {
    setSelectedTeamForCredit(team);
    setTeamCreditData({
      allocated_credit_line: team.allocated_credit_line || "",
      reason: ""
    });
    setTeamCreditDialogOpen(true);
  };

  const handleTeamCreditSubmit = () => {
    allocateTeamCreditMutation.mutate({
      teamId: selectedTeamForCredit.id,
      data: teamCreditData
    });
  };

  const handleCreditUsage = (team, operation) => {
    setSelectedTeamForCredit(team);
    setCreditUsageData({
      amount: "",
      reason: "",
      operation
    });
    setCreditUsageDialogOpen(true);
  };

  const handleCreditUsageSubmit = () => {
    manageCreditUsageMutation.mutate({
      teamId: selectedTeamForCredit.id,
      operation: creditUsageData.operation,
      data: {
        amount: creditUsageData.amount,
        reason: creditUsageData.reason
      }
    });
  };

  const handleRebalanceCredit = () => {
    if (creditSummary?.teams) {
      const initialData = creditSummary.teams.map(team => ({
        team_id: team.id,
        team_name: team.name,
        current_allocation: parseFloat(team.allocated_credit_line),
        allocation: parseFloat(team.allocated_credit_line),
        used_credit: parseFloat(team.used_credit)
      }));
      setRebalanceData(initialData);
      setRebalanceDialogOpen(true);
    }
  };

  const handleRebalanceSubmit = () => {
    const team_allocations = rebalanceData.map(item => ({
      team_id: item.team_id,
      allocation: item.allocation
    }));
    
    rebalanceCreditMutation.mutate({
      team_allocations,
      reason: "Credit rebalancing operation"
    });
  };

  const handleCreditRequest = () => {
    creditRequestMutation.mutate(creditRequestData);
  };

  const handleLegalEntityUpdate = () => {
    updateLegalEntityMutation.mutate({ legal_entity_name: legalEntityName });
  };

  const handleContractSign = () => {
    const signature = {
      signer_name: `${teamStructure?.all_members?.[0]?.first_name} ${teamStructure?.all_members?.[0]?.last_name}`,
      signer_email: teamStructure?.all_members?.[0]?.email,
      signed_at: new Date().toISOString(),
      ip_address: "system",
    };
    signContractMutation.mutate({ contractId: selectedContract.id, signature });
  };

  const downloadContract = (contract) => {
    if (contract.file?.file_url) {
      window.open(contract.file.file_url, '_blank');
    }
  };

  const getCreditStatusColor = (status) => {
    switch (status) {
      case 'active': return 'success';
      case 'suspended': return 'warning';
      case 'frozen': return 'error';
      default: return 'default';
    }
  };

  const getUtilizationColor = (percentage) => {
    if (percentage >= 90) return 'error';
    if (percentage >= 75) return 'warning';
    if (percentage >= 50) return 'info';
    return 'success';
  };

  /* ───────────────────────────────── RENDER ───────────────────────────────── */
  if (teamLoading || companyLoading) return <CircularProgress />;
  if (teamError) return <Typography color="error">Error loading team data</Typography>;

  const companyName = teamStructure?.all_members?.[0]?.company_name || "";
  const teams = teamStructure?.teams || [];
  const unassignedMembers = teamStructure?.unassigned_members || [];
  const allMembers = teamStructure?.all_members || [];
  const totalAllocationAmount = rebalanceData.reduce((sum, item) => sum + (item.allocation || 0), 0);

  return (
    <>
      <Box sx={{ width: "100%" }}>
        {/* Team Overview Stats */}
        <Card sx={{ mb: 3, background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", color: "white" }}>
          <CardContent>
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} md={2}>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                  {teamStructure?.total_teams || 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Active Teams
                </Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                  {teamStructure?.assigned_members || 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Assigned Members
                </Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                  {teamStructure?.unassigned_count || 0}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Unassigned Members
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                  ${creditSummary?.credit_summary?.total_allocated || '0'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Total Allocated Credit
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                  ${creditSummary?.credit_summary?.available_for_allocation || '0'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  Available for Allocation
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Main Content Tabs */}
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <Box sx={{ 
            borderBottom: 1, 
            borderColor: "divider",
            background: "linear-gradient(45deg, #f5f5f5 0%, #e3f2fd 100%)"
          }}>
            <Tabs 
              value={tabValue} 
              onChange={(e, newValue) => setTabValue(newValue)}
              sx={{ 
                "& .MuiTab-root": { 
                  fontWeight: 600,
                  minHeight: 56,
                  fontSize: "1rem"
                }
              }}
            >
              <Tab label="Team Structure" />
              <Tab label="Credit Management" />
              <Tab label="Credit Operations" />
              <Tab label="Legal Contracts" />
              <Tab label="Team Files" />
            </Tabs>
          </Box>

          {/* Tab 0: Team Structure */}
          {tabValue === 0 && (
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Team Structure & Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Organize team members into sub-teams for {companyName}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="contained"
                    startIcon={<CreateIcon />}
                    onClick={handleCreateTeam}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                  >
                    Create Team
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<BusinessIcon />}
                    onClick={() => {
                      setLegalEntityName(companyData?.legal_entity_name || "");
                      setLegalEntityDialogOpen(true);
                    }}
                    sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600 }}
                  >
                    Update Legal Entity
                  </Button>
                </Box>
              </Box>

              {/* Teams Display */}
              {teams.length > 0 && (
                <Box sx={{ mb: 4 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Teams ({teams.length})
                  </Typography>
                  {teams.map((team) => (
                    <Accordion key={team.id} sx={{ mb: 2, borderRadius: 2, "&:before": { display: "none" } }}>
                      <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{
                          backgroundColor: "primary.50",
                          borderRadius: 2,
                          "&.Mui-expanded": { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 }
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%" }}>
                          <GroupIcon color="primary" />
                          <Box sx={{ flex: 1 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {team.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {team.description || "No description"}
                            </Typography>
                          </Box>
                          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                            <Chip 
                              label={`${team.member_count} members`} 
                              size="small" 
                              color="primary" 
                              sx={{ fontWeight: 600 }}
                            />
                            {team.team_leader && (
                              <Chip 
                                icon={<LeaderboardIcon />}
                                label={`${team.team_leader.first_name} ${team.team_leader.last_name}`}
                                size="small" 
                                color="secondary"
                                sx={{ fontWeight: 600 }}
                              />
                            )}
                            <Chip 
                              icon={<AttachMoneyIcon />}
                              label={`$${team.allocated_credit_line || 0}`}
                              size="small" 
                              color="info"
                              sx={{ fontWeight: 600 }}
                            />
                            <Chip 
                              label={team.credit_status || 'active'}
                              size="small" 
                              color={getCreditStatusColor(team.credit_status)}
                              sx={{ fontWeight: 600 }}
                            />
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails sx={{ pt: 2 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                            Team Members
                          </Typography>
                          <Box sx={{ display: "flex", gap: 1 }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<PersonAddIcon />}
                              onClick={() => handleAssignUser(team)}
                              sx={{ textTransform: "none" }}
                            >
                              Add Member
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => handleEditTeam(team)}
                              sx={{ textTransform: "none" }}
                            >
                              Edit Team
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AttachMoneyIcon />}
                              onClick={() => handleAllocateTeamCredit(team)}
                              sx={{ textTransform: "none" }}
                            >
                              Manage Credit
                            </Button>
                          </Box>
                        </Box>

                        {team.members && team.members.length > 0 ? (
                          <Grid container spacing={2}>
                            {team.members.map((member) => (
                              <Grid item xs={12} sm={6} md={4} key={member.id}>
                                <Link
                                  to={`/user/${member.id}`}
                                  style={{ textDecoration: "none", color: "inherit" }}
                                >
                                  <Card sx={{ 
                                    border: "1px solid", 
                                    borderColor: "divider",
                                    borderRadius: 2,
                                    transition: "all 0.3s ease",
                                    position: "relative",
                                    "&:hover": { 
                                      boxShadow: 2,
                                      borderColor: "primary.main",
                                      transform: "translateY(-2px)"
                                    }
                                  }}>
                                    <CardContent sx={{ p: 2 }}>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                        <Avatar src={member.phphref} sx={{ width: 40, height: 40 }} />
                                        <Box sx={{ flex: 1 }}>
                                          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                            {member.first_name} {member.last_name}
                                          </Typography>
                                          <Typography variant="caption" color="text.secondary">
                                            {member.phone_number || "No phone"}
                                          </Typography>
                                        </Box>
                                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                          <Chip
                                            label={member.status ? "Active" : "Pending"}
                                            color={member.status ? "success" : "warning"}
                                            size="small"
                                            sx={{ fontSize: "0.7rem" }}
                                          />
                                        </Box>
                                      </Box>
                                      {!member.status && (
                                        <Button
                                          size="small"
                                          variant="contained"
                                          color="primary"
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            openDialog(member);
                                          }}
                                          sx={{ mt: 1, textTransform: "none", fontSize: "0.7rem" }}
                                          fullWidth
                                        >
                                          Activate
                                        </Button>
                                      )}
                                    </CardContent>
                                    
                                    {/* Remove user button positioned absolutely */}
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleRemoveUser(team.id, member.id);
                                      }}
                                      sx={{ 
                                        position: "absolute",
                                        top: 8,
                                        right: 8,
                                        backgroundColor: "white",
                                        boxShadow: 1,
                                        "&:hover": {
                                          backgroundColor: "error.50"
                                        }
                                      }}
                                    >
                                      <PersonRemoveIcon fontSize="small" />
                                    </IconButton>
                                  </Card>
                                </Link>
                              </Grid>
                            ))}
                          </Grid>
                        ) : (
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", py: 2 }}>
                            No members assigned to this team yet.
                          </Typography>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  ))}
                </Box>
              )}

              {/* Unassigned Members */}
              {unassignedMembers.length > 0 && (
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                    Unassigned Members ({unassignedMembers.length})
                  </Typography>
                  <Grid container spacing={2}>
                    {unassignedMembers.map((member) => (
                      <Grid item xs={12} sm={6} md={4} key={member.id}>
                        <Card sx={{ 
                          borderRadius: 3, 
                          boxShadow: 2, 
                          position: "relative",
                          border: "2px dashed",
                          borderColor: "grey.300",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: 4,
                            borderColor: "primary.main"
                          }
                        }}>
                          <Link
                            to={`/user/${member.id}`}
                            style={{ textDecoration: "none", color: "inherit" }}
                          >
                            <CardContent sx={{ textAlign: "center", p: 3 }}>
                              <Avatar
                                src={member.phphref}
                                sx={{ 
                                  width: 60, 
                                  height: 60, 
                                  mb: 2, 
                                  margin: "0 auto",
                                  border: "2px solid",
                                  borderColor: member.status ? "success.main" : "warning.main"
                                }}
                              />
                              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                {member.first_name} {member.last_name}
                              </Typography>
                              <Typography variant="caption" display="block" sx={{ mb: 2 }}>
                                {member.phone_number || "No phone number"}
                              </Typography>
                              {member.status ? (
                                <Chip 
                                  label="Active" 
                                  color="success" 
                                  size="small" 
                                  sx={{ fontWeight: 600 }}
                                />
                              ) : (
                                <Chip 
                                  label="Pending Activation" 
                                  color="warning" 
                                  size="small" 
                                  sx={{ fontWeight: 600 }}
                                />
                              )}
                            </CardContent>
                          </Link>

                          {!member.status && (
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              sx={{ 
                                position: "absolute", 
                                top: 8, 
                                right: 8,
                                borderRadius: 2,
                                textTransform: "none",
                                fontWeight: 600
                              }}
                              onClick={() => openDialog(member)}
                            >
                              Activate
                            </Button>
                          )}
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}

              {/* Add new member card */}
              <Grid container spacing={2} sx={{ mt: 2 }}>
                <Grid item xs={12} sm={6} md={4}>
                  <Link
                    to={`/register/${companyName}`}
                    style={{ textDecoration: "none", color: "inherit" }}
                  >
                    <Card
                      sx={{
                        borderRadius: 3,
                        boxShadow: 2,
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        minHeight: 200,
                        border: "2px dashed",
                        borderColor: "primary.main",
                        backgroundColor: "primary.50",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          backgroundColor: "primary.100",
                          transform: "scale(1.02)"
                        }
                      }}
                    >
                      <CardContent sx={{ textAlign: "center" }}>
                        <AddIcon sx={{ fontSize: 48, mb: 2, color: "primary.main" }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main" }}>
                          Add Team Member
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Invite new member to join {companyName}
                        </Typography>
                      </CardContent>
                    </Card>
                  </Link>
                </Grid>
              </Grid>
            </CardContent>
          )}

          {/* Tab 1: Credit Management */}
          {tabValue === 1 && (
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Team Credit Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Allocate and manage credit lines for individual teams
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<BalanceIcon />}
                  onClick={handleRebalanceCredit}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600
                  }}
                >
                  Rebalance Credit
                </Button>
              </Box>

              {/* Credit Summary Cards */}
              {creditSummary && (
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  <Grid item xs={12} md={3}>
                    <Card sx={{ 
                      borderRadius: 3,
                      background: "linear-gradient(135deg, #4caf50 0%, #81c784 100%)",
                      color: "white"
                    }}>
                      <CardContent sx={{ textAlign: "center" }}>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                          ${creditSummary.credit_summary.total_credit_line}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total Company Credit
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={{ 
                      borderRadius: 3,
                      background: "linear-gradient(135deg, #2196f3 0%, #64b5f6 100%)",
                      color: "white"
                    }}>
                      <CardContent sx={{ textAlign: "center" }}>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                          ${creditSummary.credit_summary.total_allocated}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total Allocated
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={{ 
                      borderRadius: 3,
                      background: "linear-gradient(135deg, #ff9800 0%, #ffb74d 100%)",
                      color: "white"
                    }}>
                      <CardContent sx={{ textAlign: "center" }}>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                          ${creditSummary.credit_summary.total_used}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Total Used
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Card sx={{ 
                      borderRadius: 3,
                      background: "linear-gradient(135deg, #9c27b0 0%, #ba68c8 100%)",
                      color: "white"
                    }}>
                      <CardContent sx={{ textAlign: "center" }}>
                        <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
                          ${creditSummary.credit_summary.available_for_allocation}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                          Available to Allocate
                        </Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Teams Credit Overview */}
              {creditSummary?.teams && (
                <Grid container spacing={3}>
                  {creditSummary.teams.map((team) => {
                    const utilizationPercent = team.credit_utilization_percentage || 0;
                    return (
                      <Grid item xs={12} md={6} key={team.id}>
                        <Card sx={{ 
                          borderRadius: 3, 
                          border: "1px solid", 
                          borderColor: "divider",
                          transition: "all 0.3s ease",
                          "&:hover": {
                            boxShadow: 3,
                            borderColor: "primary.main"
                          }
                        }}>
                          <CardContent sx={{ p: 3 }}>
                            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <GroupIcon sx={{ color: "primary.main", fontSize: 28 }} />
                                <Box>
                                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {team.name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {team.member_count} members
                                  </Typography>
                                </Box>
                              </Box>
                              <Chip 
                                label={team.credit_status || 'active'}
                                color={getCreditStatusColor(team.credit_status)}
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </Box>

                            <Box sx={{ mb: 2 }}>
                              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                                <Typography variant="body2" color="text.secondary">
                                  Credit Utilization
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {utilizationPercent.toFixed(1)}%
                                </Typography>
                              </Box>
                              <LinearProgress 
                                variant="determinate" 
                                value={Math.min(utilizationPercent, 100)}
                                color={getUtilizationColor(utilizationPercent)}
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </Box>

                            <Grid container spacing={2} sx={{ mb: 2 }}>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">
                                  Allocated
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: "primary.main" }}>
                                  ${team.allocated_credit_line}
                                </Typography>
                              </Grid>
                              <Grid item xs={6}>
                                <Typography variant="caption" color="text.secondary">
                                  Used
                                </Typography>
                                <Typography variant="h6" sx={{ fontWeight: 600, color: "warning.main" }}>
                                  ${team.used_credit}
                                </Typography>
                              </Grid>
                            </Grid>

                            <Typography variant="caption" display="block" sx={{ mb: 3, color: "text.secondary" }}>
                              Available: ${team.available_credit}
                            </Typography>

                            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AttachMoneyIcon />}
                                onClick={() => handleAllocateTeamCredit(team)}
                                sx={{ textTransform: "none", borderRadius: 2 }}
                              >
                                Allocate
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<TrendingUpIcon />}
                                onClick={() => handleCreditUsage(team, "use")}
                                sx={{ textTransform: "none", borderRadius: 2 }}
                              >
                                Use
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<AssignmentTurnedInIcon />}
                                onClick={() => handleCreditUsage(team, "release")}
                                sx={{ textTransform: "none", borderRadius: 2 }}
                              >
                                Release
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    );
                  })}
                </Grid>
              )}
            </CardContent>
          )}

          {/* Tab 2: Credit Operations */}
          {tabValue === 2 && (
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                    Credit Operations
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    View credit history and request changes to your credit line
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={<AccountBalanceIcon />}
                  onClick={() => setCreditDialogOpen(true)}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 600
                  }}
                >
                  Request Credit Change
                </Button>
              </Box>

              {ledgerLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ backgroundColor: "grey.50" }}>
                        <TableCell sx={{ fontWeight: 700 }}>Date</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Operation</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Change Amount</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>New Credit Line</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>New Rate</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>Legal Entity</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {creditLedger?.map((entry) => (
                        <TableRow key={entry.id} sx={{ "&:hover": { backgroundColor: "grey.50" } }}>
                          <TableCell>
                            {new Date(entry.created_at).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={entry.operation_type}
                              color={
                                entry.operation_type === "approve" ? "success" :
                                entry.operation_type === "request" ? "warning" :
                                entry.operation_type === "deny" ? "error" : "default"
                              }
                              size="small"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            ${entry.change_amount}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>
                            ${entry.new_credit_line}
                          </TableCell>
                          <TableCell>{entry.new_interest_rate}%</TableCell>
                          <TableCell>{entry.reason}</TableCell>
                          <TableCell>
                            {entry.legal_entity_name_snapshot || "—"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          )}

          {/* Tab 3: Legal Contracts */}
          {tabValue === 3 && (
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Legal Contracts & Documents
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage contracts, agreements, and legal documentation
                </Typography>
              </Box>

              {contractsLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {contracts?.map((contract) => (
                    <Grid item xs={12} md={6} key={contract.id}>
                      <Card sx={{ 
                        borderRadius: 3, 
                        border: "1px solid", 
                        borderColor: "divider",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: 3,
                          borderColor: "primary.main"
                        }
                      }}>
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <DescriptionIcon sx={{ mr: 2, color: "primary.main", fontSize: 28 }} />
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {contract.name}
                            </Typography>
                          </Box>
                          
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, minHeight: 40 }}>
                            {contract.description}
                          </Typography>
                          
                          <Typography variant="caption" display="block" sx={{ mb: 3, color: "text.secondary" }}>
                            Created: {new Date(contract.creation_date).toLocaleDateString()}
                          </Typography>
                          
                          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                            <Tooltip title="View Contract">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedContract(contract);
                                  setContractDialogOpen(true);
                                }}
                                sx={{ 
                                  backgroundColor: "primary.50",
                                  "&:hover": { backgroundColor: "primary.100" }
                                }}
                              >
                                <VisibilityIcon color="primary" />
                              </IconButton>
                            </Tooltip>
                            
                            {contract.file && (
                              <Tooltip title="Download">
                                <IconButton
                                  size="small"
                                  onClick={() => downloadContract(contract)}
                                  sx={{ 
                                    backgroundColor: "success.50",
                                    "&:hover": { backgroundColor: "success.100" }
                                  }}
                                >
                                  <DownloadIcon color="success" />
                                </IconButton>
                              </Tooltip>
                            )}

                            {(!contract.signatures || contract.signatures.length === 0) && (
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<SignatureIcon />}
                                onClick={() => {
                                  setSelectedContract(contract);
                                  setContractSignatureDialogOpen(true);
                                }}
                                sx={{ 
                                  borderRadius: 2,
                                  textTransform: "none",
                                  fontWeight: 600
                                }}
                              >
                                Sign
                              </Button>
                            )}

                            {contract.signatures && contract.signatures.length > 0 && (
                              <Chip
                                label="Signed"
                                color="success"
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            )}
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          )}

          {/* Tab 4: Team Files */}
          {tabValue === 4 && (
            <CardContent sx={{ p: 4 }}>
              <Box sx={{ mb: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                  Team Files & Documents
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Access shared files and documents for your team
                </Typography>
              </Box>

              {filesLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Grid container spacing={3}>
                  {teamFiles?.map((file) => (
                    <Grid item xs={12} sm={6} md={4} key={file.id}>
                      <Card sx={{ 
                        borderRadius: 3, 
                        border: "1px solid", 
                        borderColor: "divider",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          boxShadow: 3,
                          borderColor: "primary.main"
                        }
                      }}>
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                            <FolderIcon sx={{ mr: 2, color: "primary.main", fontSize: 28 }} />
                            <Typography variant="subtitle1" noWrap sx={{ fontWeight: 600 }}>
                              {file.filename}
                            </Typography>
                          </Box>
                          
                          <Typography variant="caption" display="block" sx={{ mb: 1, color: "text.secondary" }}>
                            Type: {file.file_type || "Unknown"}
                          </Typography>
                          
                          <Typography variant="caption" display="block" sx={{ mb: 3, color: "text.secondary" }}>
                            Uploaded: {new Date(file.uploaded_at).toLocaleDateString()}
                          </Typography>
                          
                          <Button
                            size="small"
                            variant="contained"
                            fullWidth
                            onClick={() => window.open(file.file_url, '_blank')}
                            sx={{ 
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 600
                            }}
                          >
                            Open File
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              )}
            </CardContent>
          )}
        </Card>
      </Box>

      {/* User Activation Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Confirm team-member activation?
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            By confirming{" "}
            <strong>
              {selectedUser?.first_name} {selectedUser?.last_name}
            </strong>
            , you authorize them to draw against your company's shared credit line.
          </Typography>
          <Typography variant="caption" component="p">
            View full terms{" "}
            <MuiLink component={Link} to="/terms" target="_blank">
              here
            </MuiLink>
            .
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => activateMutation.mutate(selectedUser.id)}
            disabled={activateMutation.isLoading}
            sx={{ fontWeight: 600 }}
          >
            {activateMutation.isLoading ? "Activating..." : "Agree & Activate"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Team Create/Edit Dialog */}
      <Dialog open={teamDialogOpen} onClose={() => setTeamDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {editingTeam ? "Edit Team" : "Create New Team"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Team Name"
                value={teamFormData.name}
                onChange={(e) => setTeamFormData(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={teamFormData.description}
                onChange={(e) => setTeamFormData(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Team Leader</InputLabel>
                <Select
                  value={teamFormData.team_leader_id}
                  onChange={(e) => setTeamFormData(prev => ({ ...prev, team_leader_id: e.target.value }))}
                  label="Team Leader"
                >
                  <MenuItem value="">No Leader</MenuItem>
                  {allMembers.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      {member.first_name} {member.last_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTeamSubmit}
            disabled={createTeamMutation.isLoading || updateTeamMutation.isLoading}
            sx={{ fontWeight: 600 }}
          >
            {createTeamMutation.isLoading || updateTeamMutation.isLoading ? 
              "Saving..." : (editingTeam ? "Update Team" : "Create Team")
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Assignment Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Assign User to {selectedTeamForAssignment?.name}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Select User</InputLabel>
            <Select
              value={selectedUserForAssignment}
              onChange={(e) => setSelectedUserForAssignment(e.target.value)}
              label="Select User"
            >
              {unassignedMembers.map((member) => (
                <MenuItem key={member.id} value={member.id}>
                  {member.first_name} {member.last_name} - {member.status ? "Active" : "Pending"}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleAssignSubmit}
            disabled={assignUserMutation.isLoading || !selectedUserForAssignment}
            sx={{ fontWeight: 600 }}
          >
            {assignUserMutation.isLoading ? "Assigning..." : "Assign User"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Team Credit Allocation Dialog */}
      <Dialog open={teamCreditDialogOpen} onClose={() => setTeamCreditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Allocate Credit to {selectedTeamForCredit?.name}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Allocated Credit Line"
                type="number"
                value={teamCreditData.allocated_credit_line}
                onChange={(e) => setTeamCreditData(prev => ({
                  ...prev,
                  allocated_credit_line: e.target.value
                }))}
                helperText={`Current: $${selectedTeamForCredit?.allocated_credit_line || 0} | Used: $${selectedTeamForCredit?.used_credit || 0}`}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Allocation"
                multiline
                rows={3}
                value={teamCreditData.reason}
                onChange={(e) => setTeamCreditData(prev => ({
                  ...prev,
                  reason: e.target.value
                }))}
              />
            </Grid>
            {creditSummary && (
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary">
                  Company Credit Line: ${creditSummary.credit_summary.total_credit_line}<br/>
                  Currently Available: ${creditSummary.credit_summary.available_for_allocation}
                </Typography>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamCreditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleTeamCreditSubmit}
            disabled={allocateTeamCreditMutation.isLoading}
            sx={{ fontWeight: 600 }}
          >
            {allocateTeamCreditMutation.isLoading ? "Allocating..." : "Allocate Credit"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credit Usage/Release Dialog */}
      <Dialog open={creditUsageDialogOpen} onClose={() => setCreditUsageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {creditUsageData.operation === "use" ? "Use Team Credit" : "Release Team Credit"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount"
                type="number"
                value={creditUsageData.amount}
                onChange={(e) => setCreditUsageData(prev => ({
                  ...prev,
                  amount: e.target.value
                }))}
                helperText={
                  creditUsageData.operation === "use" 
                    ? `Available: $${selectedTeamForCredit?.available_credit || 0}`
                    : `Currently Used: $${selectedTeamForCredit?.used_credit || 0}`
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason"
                multiline
                rows={3}
                value={creditUsageData.reason}
                onChange={(e) => setCreditUsageData(prev => ({
                  ...prev,
                  reason: e.target.value
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreditUsageDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreditUsageSubmit}
            disabled={manageCreditUsageMutation.isLoading}
            sx={{ fontWeight: 600 }}
          >
            {manageCreditUsageMutation.isLoading ? "Processing..." : 
             (creditUsageData.operation === "use" ? "Use Credit" : "Release Credit")
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credit Rebalancing Dialog */}
      <Dialog open={rebalanceDialogOpen} onClose={() => setRebalanceDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Rebalance Team Credit Allocations
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Adjust credit allocations across teams. Total allocation cannot exceed company credit line.
            </Typography>
            {creditSummary && (
              <Typography variant="caption" color="text.secondary">
                Company Credit Line: ${creditSummary.credit_summary.total_credit_line} | 
                New Total: ${totalAllocationAmount.toFixed(2)} | 
                Remaining: ${(parseFloat(creditSummary.credit_summary.total_credit_line) - totalAllocationAmount).toFixed(2)}
              </Typography>
            )}
          </Box>
          <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Team</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Current</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Used</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>New Allocation</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rebalanceData.map((item, index) => (
                  <TableRow key={item.team_id}>
                    <TableCell>{item.team_name}</TableCell>
                    <TableCell>${item.current_allocation}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        ${item.used_credit}
                        {item.allocation < item.used_credit && (
                          <Tooltip title="Allocation cannot be less than used credit">
                            <WarningIcon color="error" fontSize="small" />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={item.allocation}
                        onChange={(e) => {
                          const newData = [...rebalanceData];
                          newData[index].allocation = parseFloat(e.target.value) || 0;
                          setRebalanceData(newData);
                        }}
                        error={item.allocation < item.used_credit}
                        sx={{ width: 120 }}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRebalanceDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleRebalanceSubmit}
            disabled={
              rebalanceCreditMutation.isLoading ||
              (creditSummary && totalAllocationAmount > parseFloat(creditSummary.credit_summary.total_credit_line)) ||
              rebalanceData.some(item => item.allocation < item.used_credit)
            }
            sx={{ fontWeight: 600 }}
          >
            {rebalanceCreditMutation.isLoading ? "Rebalancing..." : "Apply Rebalancing"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Credit Request Dialog */}
      <Dialog open={creditDialogOpen} onClose={() => setCreditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Request Credit Line Change
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Requested Credit Line"
                type="number"
                value={creditRequestData.requested_credit_line}
                onChange={(e) => setCreditRequestData(prev => ({
                  ...prev,
                  requested_credit_line: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Requested Interest Rate (%)"
                type="number"
                value={creditRequestData.requested_interest_rate}
                onChange={(e) => setCreditRequestData(prev => ({
                  ...prev,
                  requested_interest_rate: e.target.value
                }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason for Change"
                multiline
                rows={3}
                value={creditRequestData.reason}
                onChange={(e) => setCreditRequestData(prev => ({
                  ...prev,
                  reason: e.target.value
                }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreditDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreditRequest}
            disabled={creditRequestMutation.isLoading}
            sx={{ fontWeight: 600 }}
          >
            {creditRequestMutation.isLoading ? "Submitting..." : "Submit Request"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Legal Entity Dialog */}
      <Dialog open={legalEntityDialogOpen} onClose={() => setLegalEntityDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Update Legal Entity Name
        </DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Legal Entity Name"
            value={legalEntityName}
            onChange={(e) => setLegalEntityName(e.target.value)}
            sx={{ mt: 2 }}
            helperText="This will be used in all future contracts and legal documents"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLegalEntityDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleLegalEntityUpdate}
            disabled={updateLegalEntityMutation.isLoading}
            sx={{ fontWeight: 600 }}
          >
            {updateLegalEntityMutation.isLoading ? "Updating..." : "Update"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contract View Dialog */}
      <Dialog open={contractDialogOpen} onClose={() => setContractDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {selectedContract?.name}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ 
            whiteSpace: "pre-wrap", 
            p: 3, 
            bgcolor: "grey.50", 
            borderRadius: 2,
            lineHeight: 1.6
          }}>
            {selectedContract?.contract_body}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContractDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Contract Signature Dialog */}
      <Dialog open={contractSignatureDialogOpen} onClose={() => setContractSignatureDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Sign Contract
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            By clicking "Sign Contract", you agree to the terms and conditions outlined in "{selectedContract?.name}".
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Your signature will be timestamped and legally binding.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContractSignatureDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleContractSign}
            disabled={signContractMutation.isLoading}
            sx={{ fontWeight: 600 }}
          >
            {signContractMutation.isLoading ? "Signing..." : "Sign Contract"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar feedback */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack({ ...snack, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snack.sev}
          onClose={() => setSnack({ ...snack, open: false })}
          sx={{ width: "100%" }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
};

export default MyTeam;